import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { quoteTotals, cableTypeRows, withMarkup, rateMap, money, quoteDate, VAT_RATE } from './quoteTotals';
import { MODES, kw as kwFmt, pct as pctFmt, consumerLabel, networkInUse } from './loadCalc';

const LOGO_ASPECT = 3181.136 / 927.334; // width / height
let logoCache = null; // null = not loaded, false = failed, string = data URL

export async function loadLogo() {
  if (logoCache !== null) return logoCache || null;
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      try {
        const w = 600, h = Math.round(w / LOGO_ASPECT);
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        logoCache = canvas.toDataURL('image/png');
        resolve(logoCache);
      } catch {
        logoCache = false;
        resolve(null);
      }
    };
    img.onerror = () => { logoCache = false; resolve(null); };
    img.src = '/kse-logo.svg';
  });
}

function addHeader(doc, title) {
  const pageW = doc.internal.pageSize.getWidth();
  const today = new Date().toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });

  if (logoCache) {
    const lw = 36, lh = lw / LOGO_ASPECT;
    doc.addImage(logoCache, 'PNG', pageW - 14 - lw, 5, lw, lh);
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(12);
  doc.text(title, 14, 13);

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(120);
  doc.text(today, 14, 19);
  doc.setTextColor(0);
}

const TABLE_STYLES = {
  styles:     { fontSize: 7, cellPadding: 2, overflow: 'linebreak' },
  headStyles: { fillColor: [228, 228, 228], textColor: [17, 17, 17], fontStyle: 'bold', fontSize: 7 },
  theme:      'grid',
};

// infoLines: optional text lines printed between the header and the table (e.g. test date, voltage)
// col.pdfWidth: optional fixed column width in mm (e.g. room to write readings by hand)
export async function buildTablePdf(title, cols, rows, size = 'a4', infoLines = []) {
  await loadLogo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: size });
  addHeader(doc, title);
  let y = 24;
  if (infoLines.length) {
    const maxW = doc.internal.pageSize.getWidth() - 28;
    doc.setFontSize(8);
    for (const line of infoLines) {
      const wrapped = doc.splitTextToSize(line, maxW);
      doc.text(wrapped, 14, y);
      y += wrapped.length * 3.6;
    }
    y += 2;
  }
  autoTable(doc, {
    startY: y,
    head: [cols.map(c => c.label)],
    body: rows.map(r => cols.map(c => String(c.render(r) ?? ''))),
    ...TABLE_STYLES,
    columnStyles: Object.fromEntries(cols.map((c, i) => [i, c.pdfWidth ? { cellWidth: c.pdfWidth } : {}])),
    tableWidth: 'auto',
  });
  return doc.output('blob');
}

// ── Gantt diagram PDF ────────────────────────────────────────────────────────
export async function buildGanttDiagramPdf(tasks, tlStart, tlEnd, title, colKeys = ['name','start','end','progress']) {
  await loadLogo();

  // Available left-panel columns
  const ALL_PDF_COLS = [
    { key: 'name',     label: 'Task',  width: 160 },
    { key: 'start',    label: 'Start', width: 86 },
    { key: 'end',      label: 'End',   width: 86 },
    { key: 'progress', label: '%',     width: 38 },
  ];
  // Always show name; filter to selected keys in definition order
  const activePdfCols = ALL_PDF_COLS.filter(c => c.key === 'name' || colKeys.includes(c.key));

  // Layout constants (light theme for print)
  const PDF_PPD   = 4;       // px per day — fixed for print regardless of screen zoom
  const LEFT_W    = activePdfCols.reduce((s, c) => s + c.width, 0);
  const ROW_H     = 32;
  const HDR_H     = 48;
  const HDR_TOP   = 20;
  const BAR_PAD   = 4;
  const SCALE     = 2;       // retina canvas

  const COL_WIDTHS = activePdfCols.map(c => c.width);
  const COL_LABELS = activePdfCols.map(c => c.label);

  const C = {
    bg:        '#ffffff',
    bgAlt:     '#f5f5f5',
    bgWeekend: 'rgba(0,0,0,0.04)',
    border:    '#d0d0d0',
    borderStrong: '#999999',
    hdrBg:     '#eeeeee',
    textPri:   '#111111',
    textSec:   '#444444',
    textMut:   '#888888',
    today:     '#cc0000',
    barLabel:  '#ffffff',
  };

  function daysBetween(a, b) { return Math.round((new Date(b) - new Date(a)) / 86400000); }
  function isoFmt(d) { return new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short' }); }
  function addDays(d, n) { const r = new Date(d); r.setDate(r.getDate() + n); return r; }

  const totalDays = daysBetween(tlStart, tlEnd);
  const tlWidth   = totalDays * PDF_PPD;
  const canvasW   = LEFT_W + tlWidth;
  const canvasH   = HDR_H + tasks.length * ROW_H;

  const canvas = document.createElement('canvas');
  canvas.width  = canvasW * SCALE;
  canvas.height = canvasH * SCALE;
  const ctx = canvas.getContext('2d');
  ctx.scale(SCALE, SCALE);

  // ── Background ──────────────────────────────────────────────────────────────
  ctx.fillStyle = C.bg;
  ctx.fillRect(0, 0, canvasW, canvasH);

  // ── Build header units ──────────────────────────────────────────────────────
  const monthUnits = [];
  { const cur = new Date(tlStart); cur.setDate(1);
    while (cur <= tlEnd) {
      const ms = new Date(cur);
      const nx = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
      const fr = Math.max(0, daysBetween(tlStart, ms));
      const to = Math.min(totalDays, daysBetween(tlStart, nx));
      if (to > fr) monthUnits.push({ label: ms.toLocaleDateString(undefined, { month: 'short', year: 'numeric' }), x: fr * PDF_PPD, w: (to - fr) * PDF_PPD });
      cur.setMonth(cur.getMonth() + 1);
    }
  }

  // Week tick lines (Mondays) — each entry carries x position and the week start date
  const weekTicks = [];
  { const cur = new Date(tlStart);
    const day = cur.getDay();
    if (day !== 1) cur.setDate(cur.getDate() + ((8 - day) % 7));
    while (cur <= tlEnd) { weekTicks.push({ x: daysBetween(tlStart, cur) * PDF_PPD, date: new Date(cur) }); cur.setDate(cur.getDate() + 7); }
  }

  // Day units (for weekend shading)
  const dayUnits = [];
  { const cur = new Date(tlStart);
    while (cur <= tlEnd) {
      const dow = cur.getDay();
      dayUnits.push({ x: daysBetween(tlStart, cur) * PDF_PPD, w: PDF_PPD, isWeekend: dow === 0 || dow === 6 });
      cur.setDate(cur.getDate() + 1);
    }
  }

  // ── Draw header row ─────────────────────────────────────────────────────────
  ctx.fillStyle = C.hdrBg;
  ctx.fillRect(0, 0, canvasW, HDR_H);

  // Left header cells
  ctx.fillStyle = C.textSec;
  ctx.font = `bold 9px Arial`;
  let lx = 0;
  COL_WIDTHS.forEach((w, i) => {
    if (COL_LABELS[i]) {
      ctx.fillText(COL_LABELS[i].toUpperCase(), lx + 6, HDR_H / 2 + 3);
    }
    ctx.strokeStyle = C.border;
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(lx + w, 0); ctx.lineTo(lx + w, HDR_H); ctx.stroke();
    lx += w;
  });

  // Strong left border
  ctx.strokeStyle = C.borderStrong;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(LEFT_W, 0); ctx.lineTo(LEFT_W, canvasH); ctx.stroke();

  // Month labels (top half of header)
  ctx.font = `bold 9px Arial`;
  monthUnits.forEach(u => {
    ctx.fillStyle = C.textSec;
    ctx.fillText(u.label, LEFT_W + u.x + 4, HDR_TOP / 2 + 3);
    ctx.strokeStyle = C.border;
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(LEFT_W + u.x + u.w, 0); ctx.lineTo(LEFT_W + u.x + u.w, HDR_H); ctx.stroke();
  });

  // Bottom header row separator
  ctx.strokeStyle = C.border;
  ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(LEFT_W, HDR_TOP); ctx.lineTo(canvasW, HDR_TOP); ctx.stroke();

  // Week tick lines + start-date labels in bottom half of header
  ctx.font = `7px Arial`;
  ctx.fillStyle = C.textMut;
  weekTicks.forEach(({ x: tx, date }) => {
    ctx.strokeStyle = C.borderStrong;
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(LEFT_W + tx, HDR_TOP); ctx.lineTo(LEFT_W + tx, HDR_H); ctx.stroke();
    const label = date.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
    ctx.fillStyle = C.textMut;
    ctx.fillText(label, LEFT_W + tx + 2, HDR_H - 4);
  });

  // Header bottom border
  ctx.strokeStyle = C.borderStrong;
  ctx.lineWidth = 1;
  ctx.beginPath(); ctx.moveTo(0, HDR_H); ctx.lineTo(canvasW, HDR_H); ctx.stroke();

  // ── Draw task rows ──────────────────────────────────────────────────────────
  tasks.forEach((task, idx) => {
    const rowY = HDR_H + idx * ROW_H;

    // Row background
    ctx.fillStyle = idx % 2 ? C.bgAlt : C.bg;
    ctx.fillRect(0, rowY, canvasW, ROW_H);

    // Weekend shading in timeline
    dayUnits.forEach(u => {
      if (u.isWeekend) {
        ctx.fillStyle = C.bgWeekend;
        ctx.fillRect(LEFT_W + u.x, rowY, u.w, ROW_H);
      }
    });

    // Week gridlines
    weekTicks.forEach(({ x: tx }) => {
      ctx.strokeStyle = C.border;
      ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(LEFT_W + tx, rowY); ctx.lineTo(LEFT_W + tx, rowY + ROW_H); ctx.stroke();
    });

    // Row bottom border
    ctx.strokeStyle = C.border;
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(0, rowY + ROW_H); ctx.lineTo(canvasW, rowY + ROW_H); ctx.stroke();

    // Left cell text
    ctx.fillStyle = C.textPri;
    ctx.font = `bold 9px Arial`;
    ctx.save();
    ctx.rect(0, rowY, COL_WIDTHS[0], ROW_H); ctx.clip();
    ctx.fillText(task.name || '', 6, rowY + ROW_H / 2 + 3);
    ctx.restore();

    const dateStr1 = isoFmt(task.startDate);
    const dateStr2 = isoFmt(task.endDate);
    const pctStr   = `${task.progress ?? 0}%`;
    ctx.fillStyle = C.textSec;
    ctx.font = `8px Arial`;
    ctx.fillText(dateStr1, COL_WIDTHS[0] + 4, rowY + ROW_H / 2 + 3);
    ctx.fillText(dateStr2, COL_WIDTHS[0] + COL_WIDTHS[1] + 4, rowY + ROW_H / 2 + 3);
    ctx.fillText(pctStr,   COL_WIDTHS[0] + COL_WIDTHS[1] + COL_WIDTHS[2] + 4, rowY + ROW_H / 2 + 3);

    // Left column separators
    let cx2 = 0;
    COL_WIDTHS.forEach(w => {
      cx2 += w;
      ctx.strokeStyle = C.border; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(cx2, rowY); ctx.lineTo(cx2, rowY + ROW_H); ctx.stroke();
    });

    // Bar
    const bx = Math.max(0, daysBetween(tlStart, task.startDate)) * PDF_PPD;
    const bw = Math.max(3, daysBetween(task.startDate, task.endDate) * PDF_PPD);
    const barX = LEFT_W + bx + BAR_PAD;
    const barW = Math.max(bw - BAR_PAD * 2, 3);
    const barY = rowY + 6;
    const barH = ROW_H - 12;
    const color = task.color || '#0e639c';

    ctx.fillStyle = color;
    ctx.beginPath();
    ctx.roundRect ? ctx.roundRect(barX, barY, barW, barH, 2) : ctx.rect(barX, barY, barW, barH);
    ctx.fill();

    // Progress: darken the remaining (not-done) portion
    const pct = task.progress ?? 0;
    if (pct < 100) {
      const pw = barW * (pct / 100);
      ctx.fillStyle = 'rgba(0,0,0,0.28)';
      ctx.beginPath();
      ctx.rect(barX + pw, barY, barW - pw, barH);
      ctx.fill();
    }

    // Bar label
    ctx.fillStyle = C.barLabel;
    ctx.font = `bold 8px Arial`;
    ctx.save();
    ctx.rect(barX + 2, barY, barW - 4, barH); ctx.clip();
    ctx.fillText(task.name || '', barX + 4, barY + barH / 2 + 3);
    ctx.restore();
  });

  // ── Today line ──────────────────────────────────────────────────────────────
  const todayX = daysBetween(tlStart, new Date()) * PDF_PPD;
  if (todayX >= 0 && todayX <= tlWidth) {
    ctx.strokeStyle = C.today;
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(LEFT_W + todayX, 0);
    ctx.lineTo(LEFT_W + todayX, canvasH);
    ctx.stroke();
  }

  // ── Outer border ────────────────────────────────────────────────────────────
  ctx.strokeStyle = C.borderStrong;
  ctx.lineWidth = 1;
  ctx.strokeRect(0, 0, canvasW, canvasH);

  // ── Insert into PDF ─────────────────────────────────────────────────────────
  const dataUrl = canvas.toDataURL('image/png');
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' });
  addHeader(doc, title);

  const pageW  = doc.internal.pageSize.getWidth();
  const pageH  = doc.internal.pageSize.getHeight();
  const startY = 26;
  const availW = pageW - 20;
  const availH = pageH - startY - 10;

  // Scale to fit width; if too tall, scale to fit height instead
  let imgW = availW;
  let imgH = availW * (canvasH / canvasW);
  if (imgH > availH) {
    imgH = availH;
    imgW = availH * (canvasW / canvasH);
  }

  doc.addImage(dataUrl, 'PNG', 10, startY, imgW, imgH);
  return doc.output('blob');
}

export async function buildSwitchboardPdf(projectName, boardsWithBreakers, items, size = 'a4') {
  await loadLogo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: size });
  const pageH = doc.internal.pageSize.getHeight();
  addHeader(doc, `Switchboards — ${projectName}`);

  let y = 24;

  for (const { board, breakers } of boardsWithBreakers) {
    if (y + 18 > pageH - 10) {
      doc.addPage();
      y = 14;
    }

    const boardLabel = [
      board.name,
      board.tag  ? `(${board.tag})`                                          : null,
      board.voltage                                                           ? board.voltage : null,
      board.room ? `Room ${String(board.room.number ?? '').padStart(3,'0')}` : null,
    ].filter(Boolean).join('  ·  ');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(14, 99, 156);
    doc.text(boardLabel, 14, y);
    doc.setTextColor(0);
    y += 4;

    const body = breakers.map(brk => {
      const brkId = String(brk._id);
      const item  = items.find(i => String(i.breaker?._id || i.breaker) === brkId);
      return [
        brk.tag     || '',
        brk.type    || '',
        brk.setting || '',
        item?.tag         || '',
        item?.description || '',
        item?.room ? `${String(item.room.number ?? '').padStart(3,'0')} ${item.room.text || ''}`.trim() : '',
      ];
    });

    autoTable(doc, {
      startY: y,
      head: [['Breaker', 'Type', 'Setting', 'Consumer Tag', 'Description', 'Room']],
      body,
      ...TABLE_STYLES,
    });

    y = doc.lastAutoTable.finalY + 10;
  }

  return doc.output('blob');
}

// ── Quote PDF (customer version) ────────────────────────────────────────────
// Unit prices include the cable / item markups; the markups themselves are not shown.
// opts: { cableList: per-cable list as well as the per-type summary, ean, manufacturer: extra item columns }
export async function buildQuotePdf(q, project, opts = {}) {
  await loadLogo();
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const right = pageW - 14;
  const t = quoteTotals(q);

  if (logoCache) {
    const lw = 46, lh = lw / LOGO_ASPECT;
    doc.addImage(logoCache, 'PNG', right - lw, 10, lw, lh);
  }
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.text('Quote', 14, 20);
  doc.setFontSize(12);
  doc.text(q.name || '', 14, 28);

  const info = [
    ['Quote no.', q.number || ''],
    ['Date', quoteDate(q.date)],
    ['Project', project?.name || ''],
    project?.shipName && ['Vessel', project.shipName],
    project?.newbuildingNo && ['NB no.', String(project.newbuildingNo)],
  ].filter(Boolean);
  doc.setFontSize(9);
  let y = 36;
  for (const [label, value] of info) {
    doc.setFont('helvetica', 'bold'); doc.text(label, 14, y);
    doc.setFont('helvetica', 'normal'); doc.text(value, 40, y);
    y += 5;
  }
  y += 3;

  const tableOpts = {
    ...TABLE_STYLES,
    styles: { ...TABLE_STYLES.styles, fontSize: 8 },
    headStyles: { ...TABLE_STYLES.headStyles, fontSize: 8 },
    footStyles: { fillColor: [245, 245, 245], textColor: [17, 17, 17], fontStyle: 'bold', fontSize: 8 },
    margin: { left: 14, right: 14 },
  };
  const num = { halign: 'right' };
  const section = (title) => {
    if (y > doc.internal.pageSize.getHeight() - 30) { doc.addPage(); y = 20; }
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text(title, 14, y);
    y += 2;
  };
  // Numeric columns are right-aligned in the head, body and total row alike
  const table = (head, body, foot, columnStyles) => {
    autoTable(doc, {
      ...tableOpts, startY: y, head: [head], body, foot: foot ? [foot] : undefined, showFoot: 'lastPage', columnStyles,
      didParseCell: (data) => { if (columnStyles?.[data.column.index]?.halign) data.cell.styles.halign = columnStyles[data.column.index].halign; },
    });
    y = doc.lastAutoTable.finalY + 8;
  };

  // Cables, summed per cable type
  if (q.cables?.length) {
    section('Cables');
    const rows = cableTypeRows(q);
    table(
      ['Cable type', 'Cables', 'Length [m]', 'Price/m', 'Amount'],
      rows.map(r => {
        const unit = withMarkup(r.price, q.cableMarkup);
        return [r.cableType || '(no type)', r.count, money(r.length).replace(/,00$/, ''), money(unit), money(unit * r.length)];
      }),
      ['Total cables', '', '', '', money(t.cables)],
      { 1: num, 2: num, 3: num, 4: num },
    );
    if (opts.cableList) {
      section('Cable list');
      const price = new Map(rows.map(r => [r.cableType, withMarkup(r.price, q.cableMarkup)]));
      table(
        ['Cable', 'Type', 'From', 'To', 'Length [m]', 'Amount'],
        q.cables.map(c => [c.tag || '', c.cableType || '', c.from || '', c.to || '', c.length_m ?? '',
          money((c.length_m || 0) * (price.get(c.cableType || '') || 0))]),
        null,
        { 4: num, 5: num },
      );
    }
  }

  // Materials, one table per .sep file / group
  for (const g of q.itemGroups || []) {
    if (!g.items?.length) continue;
    section(`Materials — ${g.name || g.fileName || 'Items'}`);
    const cols = [
      ['Qty', i => i.qty ?? ''],
      ['Article', i => i.type || ''],
      ['Description', i => i.description || ''],
      opts.manufacturer && ['Manufacturer', i => i.manufacturer || ''],
      opts.ean && ['EAN', i => i.ean || ''],
      ['Unit price', i => money(withMarkup(i.price, q.itemMarkup))],
      ['Amount', i => money(withMarkup(i.price, q.itemMarkup) * (i.qty || 0))],
    ].filter(Boolean);
    const groupTotal = g.items.reduce((s, i) => s + withMarkup(i.price, q.itemMarkup) * (i.qty || 0), 0);
    const n = cols.length;
    table(
      cols.map(c => c[0]),
      g.items.map(i => cols.map(c => c[1](i))),
      ['Total', ...Array(n - 2).fill(''), money(groupTotal)],
      { 0: num, [n - 2]: num, [n - 1]: num },
    );
  }

  // Labour
  if (q.hours?.length) {
    section('Labour');
    const rates = rateMap(q);
    table(
      ['Description', 'Category', 'Hours', 'Rate', 'Amount'],
      q.hours.map(h => {
        const r = rates.get(h.rateKey);
        return [h.description || '', r?.name || '', h.hours ?? '', money(r?.rate), money((h.hours || 0) * (r?.rate || 0))];
      }),
      ['Total labour', '', '', '', money(t.hours)],
      { 2: num, 3: num, 4: num },
    );
  }

  // Totals
  const lines = [
    q.cables?.length && ['Cables', money(t.cables)],
    q.itemGroups?.some(g => g.items?.length) && ['Materials', money(t.items)],
    q.hours?.length && ['Labour', money(t.hours)],
    t.discount > 0 && ['Subtotal', money(t.subtotal)],
    t.discount > 0 && [`Discount ${q.discount} %`, `-${money(t.discount)}`],
    ['Total excl. VAT', money(t.net), true],
    q.vat && [`VAT ${VAT_RATE * 100} %`, money(t.vat)],
    q.vat && ['Total incl. VAT (DKK)', money(t.total), true],
  ].filter(Boolean);
  if (y > doc.internal.pageSize.getHeight() - 12 - lines.length * 6) { doc.addPage(); y = 20; }
  doc.setFontSize(10);
  for (const [label, value, bold] of lines) {
    doc.setFont('helvetica', bold ? 'bold' : 'normal');
    doc.text(label, right - 75, y);
    doc.text(value, right, y, { align: 'right' });
    if (bold) doc.line(right - 75, y + 1.5, right, y + 1.5);
    y += 6;
  }
  if (!q.vat) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(120);
    doc.text('All prices in DKK, excl. VAT.', right, y, { align: 'right' });
    doc.setTextColor(0);
  }

  // Page numbers
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(140);
    doc.text(`${q.number || ''}  ·  page ${p} of ${pages}`, right, doc.internal.pageSize.getHeight() - 8, { align: 'right' });
    doc.setTextColor(0);
  }
  return doc.output('blob');
}

// ── Load calculation PDF ─────────────────────────────────────────────────────
// view: 'balance' | 'summary' | 'detailed'; keys: mode keys to print (+ 'location', 'nel' for detailed)
export async function buildLoadCalcPdf(view, data, project, keys = []) {
  await loadLogo();
  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageH = doc.internal.pageSize.getHeight();
  const pageW = doc.internal.pageSize.getWidth();
  const modes = MODES.filter(m => keys.includes(m.key));
  const viewTitle = { balance: 'Power balance', summary: 'Summarized', detailed: 'Detailed' }[view];
  const vessel = [project?.name, project?.shipName && project.shipName !== project?.name ? `'${project.shipName}'` : null].filter(Boolean).join(' ');
  const newPage = () => { doc.addPage(); y = 32; };

  const RED = [200, 30, 30];
  const num = { halign: 'right' };
  const modeHead = modes.map(m => m.label.toUpperCase());
  const modeCells = (loads, d = 1) => modes.map(m => kwFmt(loads?.[m.key], d));
  let y = 32;
  // The built-in PDF font is WinAnsi only (no →, φ, Ω)
  const clean = (v) => (typeof v === 'string'
    ? v.replace(/→/g, '->').replace(/[φΦ]/g, 'phi').replace(/Ω/g, 'Ohm').replace(/[^\x00-\xFF\u2013\u2014\u2022\u2026\u20AC]/g, '?')
    : v);

  const table = (head, body, opts = {}) => {
    const numCols = opts.numFrom ?? 1;
    autoTable(doc, {
      startY: y,
      head: [head],
      body,
      ...TABLE_STYLES,
      theme: 'plain',
      styles: { ...TABLE_STYLES.styles, fontSize: 7.5, cellPadding: 1.4 },
      headStyles: { fontStyle: 'bold', fontSize: 7, textColor: [90, 90, 90] },
      margin: { left: 14, right: 14, top: opts.continued ? 34 : 28, bottom: 14 },
      columnStyles: opts.columnStyles,
      // Name the switchboard again on the pages a table runs over to
      didDrawPage: (d) => {
        if (!opts.continued || d.pageNumber === 1) return;
        doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
        doc.text(`${opts.continued} (continued)`, 14, 30);
      },
      didParseCell: (c) => {
        const col = c.column.index;
        if (col >= numCols && !opts.columnStyles?.[col]?.halign) c.cell.styles.halign = 'right';
        const modeIdx = col - (head.length - modes.length);
        if (modeIdx >= 0 && modes[modeIdx]?.key === 'emergency') c.cell.styles.textColor = RED;
        const raw = c.row.raw;
        if (c.section === 'body' && raw?.bold) c.cell.styles.fontStyle = 'bold';
        if (c.section === 'body' && raw?.muted) c.cell.styles.textColor = [120, 120, 120];
        if (c.section === 'body' && raw?.italic) c.cell.styles.fontStyle = 'italic';
        if (c.section === 'body' && raw?.line) { c.cell.styles.lineWidth = { top: 0.3 }; c.cell.styles.lineColor = [60, 60, 60]; }
      },
    });
    y = doc.lastAutoTable.finalY + 8;
  };
  // autoTable body rows carrying style flags
  const row = (cells, flags = {}) => Object.assign(cells.map(clean), flags);
  // A label spanning the first n columns (totals rows)
  const span = (content, n) => ({ content: clean(content), colSpan: n });
  const heading = (text, sub) => {
    if (y > pageH - 40) newPage();
    text = clean(text); sub = clean(sub);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(11);
    doc.text(text, 14, y);
    if (sub) {
      const x = 14 + doc.getTextWidth(text) + 4;
      doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(110);
      doc.text(sub, x, y);
      doc.setTextColor(0);
    }
    y += 2;
  };

  if (view === 'summary') {
    table(['SWITCHBOARD', 'CONSUMERS', 'INSTALLED (KW)', ...modeHead], [
      ...data.boards.map(b => row([`${b.name}${b.description ? ` — ${b.description}` : ''}`, b.own.count, kwFmt(b.own.installed), ...modeCells(b.own.loads)])),
      row(['Total power consumption', data.totals.count, kwFmt(data.totals.installed), ...modeCells(data.totals.loads)], { bold: true, line: true }),
    ]);
  }

  if (view === 'detailed') {
    const loc = keys.includes('location'), nel = keys.includes('nel');
    data.boards.forEach((b, i) => {
      if (i > 0) newPage();
      heading(`Switchboard: ${b.name}${b.description ? ` — ${b.description}` : ''}`,
        [b.voltage, b.fedFrom && `fed from ${b.fedFrom.tag} (${b.fedFrom.board})`].filter(Boolean).join(' · '));
      const head = ['BR NO', 'CONSUMER', loc && 'LOCATION', 'POWER (KW)', nel && 'NEL TRIP', ...modeHead].filter(Boolean);
      const body = [
        ...b.rows.map(r => row([r.breaker, consumerLabel(r) + (r.replaced ? ' (see calculated supply)' : ''), loc && r.location,
          kwFmt(r.installed, 2), nel && (r.nelTrip ? 'YES' : ''), ...(r.replaced ? modes.map(() => '') : modeCells(r.loads, 2))].filter(x => x !== false), { muted: r.replaced })),
        ...b.feeds.map(f => row([f.breaker, f.description, loc && '', kwFmt(f.installed, 2), nel && '', ...modeCells(f.loads, 2)].filter(x => x !== false), { italic: true })),
        row([span(`Totals for ${b.name} (${b.own.count} consumers)`, loc ? 3 : 2), kwFmt(b.own.installed), nel && '', ...modeCells(b.own.loads)].filter(x => x !== false), { bold: true, line: true }),
        ...(b.feeds.length ? [row([span('Incl. supplied boards', loc ? 3 : 2), kwFmt(b.total.installed), nel && '', ...modeCells(b.total.loads)].filter(x => x !== false), { bold: true })] : []),
      ];
      const fixed = 2 + (loc ? 1 : 0);
      table(head, body, { numFrom: fixed, continued: `Switchboard: ${b.name}`, columnStyles: { 0: { cellWidth: 18 }, 1: { cellWidth: loc ? 80 : 110 }, ...(nel ? { [fixed + 1]: { halign: 'center' } } : {}) } });
    });
  }

  if (view === 'balance') {
    data.networks.filter(networkInUse).forEach((n, i) => {
      if (i > 0 && y > pageH - 80) newPage();
      heading(`${n.name}${n.voltage ? ` · ${n.voltage}` : ''}`, n.boards.length > 1 ? `Boards: ${n.boards.join(', ')}` : '');
      const body = [
        row(['Consumers', kwFmt(n.consumersInstalled), ...modeCells(n.consumers)]),
        ...n.supplies.map(s => row([`   ${s.name}`, '', ...modeCells(s.loads)])),
        row(['Total power consumption', kwFmt(n.installed), ...modeCells(n.consumption)], { bold: true, line: true }),
      ];
      if (n.nelRows.length) {
        body.push(
          ...n.nelRows.map(r => row([`   ${consumerLabel(r)}`, kwFmt(r.installed), ...modeCells(r.loads)], { muted: true })),
          row(['Non-essential loads (NEL trip)', '', ...modeCells(n.nelLoads)]),
          row(['Consumption with NEL trip', '', ...modeCells(n.afterNel)], { bold: true, line: true }),
        );
      }
      body.push(row(['INSTALLED SOURCES', '', ...modes.map(() => '')], { muted: true }));
      body.push(...n.sources.map(s => row([`   ${s.name}`, kwFmt(s.available), ...modeCells(s.perMode)])));
      body.push(
        row(['Available power', '', ...modeCells(n.available)], { bold: true, line: true }),
        row(['Load', '', ...modes.map(m => pctFmt(n.loadPct[m.key]))]),
        ...(n.nelRows.length ? [row(['Load with NEL trip', '', ...modes.map(m => pctFmt(n.loadPctNel[m.key]))])] : []),
        row(['Spare power', '', ...modeCells(n.margin)]),
      );
      table(['KW', 'INSTALLED', ...modeHead], body, { columnStyles: { 0: { cellWidth: 110 } } });
    });
    doc.setFontSize(7); doc.setTextColor(120);
    doc.text('All values in kW. Transformer / UPS loads include their efficiency.', 14, Math.min(y, pageH - 14));
    doc.setTextColor(0);
  }

  // Header, vessel and page number on every page
  const pages = doc.getNumberOfPages();
  for (let p = 1; p <= pages; p++) {
    doc.setPage(p);
    addHeader(doc, `Electrical load calculation — ${viewTitle}`);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(9);
    doc.text(clean(vessel), 14, 23);
    doc.setFontSize(7); doc.setTextColor(140);
    doc.text(`Page ${p} / ${pages}`, pageW - 14, pageH - 8, { align: 'right' });
    doc.setTextColor(0);
  }
  return doc.output('blob');
}
